import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useLanguage } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  ArrowLeft, 
  DollarSign,
  Home,
  Target,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  TrendingUp,
  Shuffle,
  User,
  Filter,
  CreditCard,
  ExternalLink,
  Loader2
} from "lucide-react";

export default function PaymentsPage({ evaluator }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [players, setPlayers] = useState([]);
  const [gamedays, setGamedays] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlayer, setFilterPlayer] = useState("all");
  
  // Stripe states
  const [stripeDialogOpen, setStripeDialogOpen] = useState(false);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [selectedPlayerForStripe, setSelectedPlayerForStripe] = useState("");
  const [isProcessingStripe, setIsProcessingStripe] = useState(false);
  const [stripeTransactions, setStripeTransactions] = useState([]);
  
  const [newPayment, setNewPayment] = useState({
    player_id: "",
    amount: "",
    date: new Date().toISOString().split('T')[0],
    description: "",
    status: "pendente",
    gameday_id: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [paymentsRes, playersRes, gamedaysRes, summaryRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/payments`),
        axios.get(`${API}/players`),
        axios.get(`${API}/gamedays`),
        axios.get(`${API}/payments/summary`),
        axios.get(`${API}/payments/stripe/transactions`).catch(() => ({ data: [] }))
      ]);
      setPayments(paymentsRes.data);
      setPlayers(playersRes.data);
      setGamedays(gamedaysRes.data);
      setSummary(summaryRes.data);
      setStripeTransactions(transactionsRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchPackages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payments/packages`);
      setPackages(res.data);
    } catch (error) {
      console.error("Error loading packages:", error);
    }
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error("Tempo esgotado ao verificar pagamento. Verifique seu email.");
      window.history.replaceState({}, '', '/pagamentos');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success("Pagamento confirmado com sucesso!");
        fetchData();
        window.history.replaceState({}, '', '/pagamentos');
        return;
      } else if (response.data.status === 'expired') {
        toast.error("Sessão de pagamento expirada");
        window.history.replaceState({}, '', '/pagamentos');
        return;
      }
      
      // Continue polling
      toast.info("Verificando pagamento...");
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      toast.error("Erro ao verificar pagamento");
      window.history.replaceState({}, '', '/pagamentos');
    }
  }, [fetchData]);

  // Check for Stripe return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error("Pagamento cancelado");
      window.history.replaceState({}, '', '/pagamentos');
    }
  }, [searchParams, pollPaymentStatus]);

  useEffect(() => {
    fetchData();
    fetchPackages();
  }, [fetchData, fetchPackages]);

  const getPlayerName = (player) => {
    if (!player) return "Desconhecido";
    if (player.first_name) return `${player.first_name} ${player.last_name}`;
    return player.name || '';
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    if (!newPayment.player_id || !newPayment.amount || !newPayment.date) {
      toast.error("Preencha jogador, valor e data");
      return;
    }

    try {
      const paymentData = {
        ...newPayment,
        amount: parseFloat(newPayment.amount),
        gameday_id: newPayment.gameday_id || null
      };

      if (editingPayment) {
        await axios.put(`${API}/payments/${editingPayment.id}`, paymentData);
        toast.success("Pagamento atualizado!");
      } else {
        await axios.post(`${API}/payments`, paymentData);
        toast.success("Pagamento registrado!");
      }
      setDialogOpen(false);
      setEditingPayment(null);
      setNewPayment({
        player_id: "",
        amount: "",
        date: new Date().toISOString().split('T')[0],
        description: "",
        status: "pendente",
        gameday_id: ""
      });
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar pagamento");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Deseja excluir este pagamento?")) return;
    try {
      await axios.delete(`${API}/payments/${paymentId}`);
      toast.success("Pagamento excluído!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir pagamento");
    }
  };

  const handleToggleStatus = async (payment) => {
    try {
      const newStatus = payment.status === "pago" ? "pendente" : "pago";
      await axios.put(`${API}/payments/${payment.id}`, { status: newStatus });
      toast.success(`Status alterado para ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Erro ao alterar status");
    }
  };

  // Stripe checkout
  const handleStripeCheckout = async () => {
    if (!selectedPlayerForStripe || !selectedPackage) {
      toast.error("Selecione um jogador e um pacote de pagamento");
      return;
    }

    setIsProcessingStripe(true);
    try {
      const response = await axios.post(`${API}/payments/stripe/checkout`, {
        player_id: selectedPlayerForStripe,
        package_id: selectedPackage,
        origin_url: window.location.origin
      });

      if (response.data.checkout_url) {
        toast.success("Redirecionando para o checkout...");
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar sessão de pagamento");
    } finally {
      setIsProcessingStripe(false);
    }
  };

  const openStripeDialog = () => {
    setSelectedPlayerForStripe("");
    setSelectedPackage("");
    setStripeDialogOpen(true);
  };

  const openEditDialog = (payment) => {
    setEditingPayment(payment);
    setNewPayment({
      player_id: payment.player_id,
      amount: payment.amount.toString(),
      date: payment.date,
      description: payment.description || "",
      status: payment.status,
      gameday_id: payment.gameday_id || ""
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingPayment(null);
    setNewPayment({
      player_id: "",
      amount: "",
      date: new Date().toISOString().split('T')[0],
      description: "",
      status: "pendente",
      gameday_id: ""
    });
    setDialogOpen(true);
  };

  const filteredPayments = payments.filter(p => {
    if (filterStatus !== "all" && p.status !== filterStatus) return false;
    if (filterPlayer !== "all" && p.player_id !== filterPlayer) return false;
    return true;
  });

  const canEdit = evaluator?.role === 'admin' || evaluator?.role === 'tecnico';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">{t('paymentsAdmin.title')}</h1>
                <p className="text-sm text-muted-foreground">Controle de pagamentos por jogador</p>
              </div>
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <Button onClick={openStripeDialog} variant="outline" className="border-purple-500 text-purple-400 hover:bg-purple-500/10" data-testid="stripe-payment-btn">
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pagar com Cartão
                </Button>
                <Button onClick={openCreateDialog} data-testid="new-payment-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('paymentsAdmin.newPayment')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Carregando...</div>
        ) : (
          <div className="space-y-6">
            {/* Summary Cards */}
            {summary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-card border-border/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Pago</p>
                        <p className="text-2xl font-bold text-green-400">
                          R$ {summary.total_paid?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{t('paymentsAdmin.totalPending')}</p>
                        <p className="text-2xl font-bold text-yellow-400">
                          R$ {summary.total_pending?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <Clock className="h-8 w-8 text-yellow-400" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Total Geral</p>
                        <p className="text-2xl font-bold text-primary">
                          R$ {summary.total_revenue?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-primary" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Payments List */}
            <Card className="bg-card border-border/50 shadow-xl">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-primary" />
                    Lista de Pagamentos
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-32 bg-input/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="pago">Pagos</SelectItem>
                        <SelectItem value="pendente">Pendentes</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={filterPlayer} onValueChange={setFilterPlayer}>
                      <SelectTrigger className="w-40 bg-input/50">
                        <SelectValue placeholder="Jogador" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {players.map(p => (
                          <SelectItem key={p.id} value={p.id}>{getPlayerName(p)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredPayments.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">{t('paymentsAdmin.noPayments')}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50">
                        <TableHead>Jogador</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Descrição</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredPayments.map(payment => (
                        <TableRow key={payment.id} className="border-border/50">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <User className="h-4 w-4 text-primary" />
                              </div>
                              {payment.player_name || "Desconhecido"}
                            </div>
                          </TableCell>
                          <TableCell>{payment.date}</TableCell>
                          <TableCell>{payment.description || "-"}</TableCell>
                          <TableCell className="text-right font-bold">
                            R$ {payment.amount.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={payment.status === "pago" ? "default" : "secondary"}
                              className={`cursor-pointer ${payment.status === "pago" ? "bg-green-500" : "bg-yellow-500"}`}
                              onClick={() => canEdit && handleToggleStatus(payment)}
                            >
                              {payment.status === "pago" ? (
                                <><CheckCircle className="h-3 w-3 mr-1" />Pago</>
                              ) : (
                                <><Clock className="h-3 w-3 mr-1" />Pendente</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {canEdit && (
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openEditDialog(payment)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeletePayment(payment.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* By Player Summary */}
            {summary?.by_player && summary.by_player.length > 0 && (
              <Card className="bg-card border-border/50 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-lg font-bold">Resumo por Jogador</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-border/50">
                        <TableHead>Jogador</TableHead>
                        <TableHead className="text-right">Pago</TableHead>
                        <TableHead className="text-right">Pendente</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {summary.by_player.map(ps => (
                        <TableRow key={ps.player_id} className="border-border/50">
                          <TableCell className="font-medium">{ps.player_name}</TableCell>
                          <TableCell className="text-right text-green-400">R$ {ps.paid.toFixed(2)}</TableCell>
                          <TableCell className="text-right text-yellow-400">R$ {ps.pending.toFixed(2)}</TableCell>
                          <TableCell className="text-right font-bold">R$ {(ps.paid + ps.pending).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editingPayment ? t('paymentsAdmin.newPayment') : t('paymentsAdmin.newPayment')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreatePayment} className="space-y-4">
            <div className="space-y-2">
              <Label>Jogador *</Label>
              <Select value={newPayment.player_id} onValueChange={(v) => setNewPayment({ ...newPayment, player_id: v })}>
                <SelectTrigger className="bg-input/50">
                  <SelectValue placeholder="Selecione o jogador" />
                </SelectTrigger>
                <SelectContent>
                  {players.map(p => (
                    <SelectItem key={p.id} value={p.id}>{getPlayerName(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valor (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newPayment.amount}
                  onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                  placeholder="0.00"
                  className="bg-input/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={newPayment.date}
                  onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                  className="bg-input/50"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={newPayment.status} onValueChange={(v) => setNewPayment({ ...newPayment, status: v })}>
                <SelectTrigger className="bg-input/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Vincular ao Jogo (opcional)</Label>
              <Select value={newPayment.gameday_id || "none"} onValueChange={(v) => setNewPayment({ ...newPayment, gameday_id: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-input/50">
                  <SelectValue placeholder="Nenhum" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {gamedays.map(gd => (
                    <SelectItem key={gd.id} value={gd.id}>{gd.date}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                value={newPayment.description}
                onChange={(e) => setNewPayment({ ...newPayment, description: e.target.value })}
                placeholder="Ex: Mensalidade Janeiro"
                className="bg-input/50"
              />
            </div>
            <Button type="submit" className="w-full bg-primary">
              {editingPayment ? "Salvar" : "Registrar Pagamento"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stripe Payment Dialog */}
      <Dialog open={stripeDialogOpen} onOpenChange={setStripeDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-400" />
              Pagamento com Cartão
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Selecione o Jogador *</Label>
              <Select value={selectedPlayerForStripe} onValueChange={setSelectedPlayerForStripe}>
                <SelectTrigger className="bg-input/50">
                  <SelectValue placeholder="Selecione um jogador" />
                </SelectTrigger>
                <SelectContent>
                  {players.map(p => (
                    <SelectItem key={p.id} value={p.id}>{getPlayerName(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Selecione o Pacote *</Label>
              <div className="grid gap-2">
                {packages.map(pkg => (
                  <div
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedPackage === pkg.id
                        ? 'border-purple-500 bg-purple-500/10'
                        : 'border-border hover:border-purple-500/50'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{pkg.description}</span>
                      <span className="text-lg font-bold text-purple-400">
                        R$ {pkg.amount.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStripeDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleStripeCheckout}
              disabled={isProcessingStripe || !selectedPlayerForStripe || !selectedPackage}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isProcessingStripe ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Ir para Checkout
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Home className="h-5 w-5" />
          <span className="text-xs">Início</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/payments')} className="flex flex-col items-center gap-1 h-auto py-2 text-primary">
          <DollarSign className="h-5 w-5" />
          <span className="text-xs">{t('paymentsAdmin.title')}</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/gameday')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Shuffle className="h-5 w-5" />
          <span className="text-xs">Times</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/evaluate')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Target className="h-5 w-5" />
          <span className="text-xs">Avaliar</span>
        </Button>
      </nav>
    </div>
  );
}
